/**
 * Adapter that lets the generic ``useChatFileUpload`` lifecycle hook
 * drive the **Projects** attachment endpoints.
 *
 * The Evidence chat composer wires ``useChatFileUpload`` against
 * ``POST /chat/attachments``; we want the same UX (drag-drop, paste,
 * progress, cancel, retry, server-side delete on chip-remove) on the
 * Projects composer but pointed at the project's pre-upload route
 * (``POST /projects/{id}/attachments``). Rather than fork the hook,
 * ``useChatFileUpload`` accepts an ``adapter`` option — this module
 * is what we pass in.
 *
 * The mutation is keyed by ``projectId`` so two open projects don't
 * trample each other's react-query cache, and it tracks per-file
 * AbortControllers so the chip's "remove during upload" path can
 * cancel the in-flight request the same way Evidence does.
 */

import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';

import { projectsClient } from '../api/projects';

/** The upload lifecycle's adapter contract, structurally (the app hook types it the same way). */
export interface ProjectFileUploadAdapter {
  uploadAttachment: (variables: {
    uid: string;
    file: File;
    onProgressUpdate?: (progress: number) => void;
  }) => Promise<{ id: string; url: string }>;
  deleteAttachment: (attachmentId: string) => Promise<void>;
  /** Cancel an in-flight upload by ``uid`` (returns true if cancelled). */
  cancelUpload: (uid: string) => boolean;
}

const UPLOAD_PROJECT_ATTACHMENT = 'uploadProjectAttachment';
const DELETE_PROJECT_ATTACHMENT = 'deleteProjectAttachment';

/**
 * Per-process map of in-flight project uploads, keyed by file uid.
 *
 * Lives at module scope (mirroring Evidence's
 * ``activeUploadControllers``) so ``cancelUpload(uid)`` can find the
 * controller from anywhere in the React tree without threading a ref
 * through every consumer.
 */
const activeProjectUploadControllers = new Map<string, AbortController>();

const cancelProjectAttachmentUpload = (uid: string): boolean => {
  const controller = activeProjectUploadControllers.get(uid);
  if (controller) {
    controller.abort();
    activeProjectUploadControllers.delete(uid);
    return true;
  }
  return false;
};

interface UploadProjectAttachmentVariables {
  uid: string;
  file: File;
  onProgressUpdate?: (progress: number) => void;
}

/**
 * Mutation hook for uploading a single attachment to a Project's
 * durable run. Mirrors ``useMutateUploadChatAttachment`` so the
 * generic ``useChatFileUpload`` lifecycle works without changes.
 */
const useMutateUploadProjectAttachment = (projectId: string) =>
  useMutation({
    // Project-id-scoped key so two projects' uploads stay isolated in
    // the cache. Two projects open at once shouldn't see each other's
    // pending mutations.
    mutationKey: [UPLOAD_PROJECT_ATTACHMENT, projectId],
    mutationFn: async ({
      uid,
      file,
      onProgressUpdate,
    }: UploadProjectAttachmentVariables) => {
      const controller = new AbortController();
      activeProjectUploadControllers.set(uid, controller);
      try {
        const response = await projectsClient.uploadAttachment({
          projectId,
          file,
          onProgressUpdate,
          signal: controller.signal,
        });
        return response.data;
      } finally {
        activeProjectUploadControllers.delete(uid);
      }
    },
    onError: (error: Error, variables) => {
      // Clinical filenames frequently encode PHI ("smith_john_dob1980_labs.pdf")
      // — log only the opaque uid + file extension + size so failures are
      // diagnosable without leaking patient identifiers into logs/telemetry.
      const ext = (() => {
        const dot = variables.file.name.lastIndexOf('.');
        return dot >= 0 ? variables.file.name.slice(dot).toLowerCase() : '';
      })();
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        logger.debug(`Project attachment upload cancelled for ${variables.uid}`);
      } else {
        // A fixed message: an upload error can echo the filename or the response body.
        logger.error('Error uploading project attachment', {
          projectId,
          uid: variables.uid,
          fileExt: ext,
          fileSize: variables.file.size,
          error: sanitizeError(error),
        });
      }
    },
  });

const useMutateDeleteProjectAttachment = (projectId: string) =>
  useMutation({
    mutationKey: [DELETE_PROJECT_ATTACHMENT, projectId],
    mutationFn: async (attachmentId: string): Promise<void> => {
      await projectsClient.deleteAttachment(projectId, attachmentId);
    },
    onError: (error: Error, attachmentId) => {
      logger.error('Error deleting project attachment', {
        projectId,
        attachmentId,
        error: sanitizeError(error),
      });
    },
  });

/**
 * Returns an adapter usable as ``useChatFileUpload({ adapter })``.
 *
 * The hook calls into the project upload + delete + cancel paths
 * exclusively — no chat-attachment requests fire when this adapter
 * is in play. The shape (``{ id, url }``) intentionally matches what
 * the chat-attachment endpoint returns so the lifecycle code never
 * needs to discriminate.
 */
export const useProjectFileUploadAdapter = (
  projectId: string
): ProjectFileUploadAdapter => {
  const { mutateAsync: uploadAttachment } = useMutateUploadProjectAttachment(projectId);
  const { mutateAsync: deleteAttachment } = useMutateDeleteProjectAttachment(projectId);

  return useMemo<ProjectFileUploadAdapter>(
    () => ({
      uploadAttachment: async ({ uid, file, onProgressUpdate }) => {
        const result = await uploadAttachment({ uid, file, onProgressUpdate });
        return { id: result.id, url: result.url };
      },
      deleteAttachment,
      cancelUpload: cancelProjectAttachmentUpload,
    }),
    [uploadAttachment, deleteAttachment]
  );
};

/**
 * The Chat attachment upload stack (``POST /chat/attachments``), which Chat
 * owns and the app supplies: its two mutation hooks and its cancel. Pass one
 * module-level object so the hooks and the cancel keep their identity.
 */
export interface ChatAttachmentUploadPorts {
  useUploadAttachment: () => {
    mutateAsync: ProjectFileUploadAdapter['uploadAttachment'];
  };
  useDeleteAttachment: () => {
    mutateAsync: ProjectFileUploadAdapter['deleteAttachment'];
  };
  cancelUpload: ProjectFileUploadAdapter['cancelUpload'];
}

/**
 * Adapter for ``useChatFileUpload`` on surfaces that pre-upload via
 * ``POST /chat/attachments`` — Work home before a project exists, and
 * Evidence chat. Mirrors ``useProjectFileUploadAdapter`` so the generic
 * upload lifecycle stays shared.
 */
export const useChatAttachmentUploadAdapter = ({
  useUploadAttachment,
  useDeleteAttachment,
  cancelUpload,
}: ChatAttachmentUploadPorts): ProjectFileUploadAdapter => {
  const { mutateAsync: uploadAttachment } = useUploadAttachment();
  const { mutateAsync: deleteAttachment } = useDeleteAttachment();

  return useMemo<ProjectFileUploadAdapter>(
    () => ({
      uploadAttachment,
      deleteAttachment,
      cancelUpload,
    }),
    [uploadAttachment, deleteAttachment, cancelUpload]
  );
};

/**
 * Picks the attachment upload adapter for the Projects composer:
 * project-scoped pre-upload when a run exists, chat attachments on Work home.
 */
export const useComposerAttachmentUploadAdapter = (
  projectId: string | undefined,
  chatUploadPorts: ChatAttachmentUploadPorts
): ProjectFileUploadAdapter => {
  const chatAdapter = useChatAttachmentUploadAdapter(chatUploadPorts);
  const projectAdapter = useProjectFileUploadAdapter(projectId ?? '');

  return useMemo(
    () => (projectId ? projectAdapter : chatAdapter),
    [chatAdapter, projectAdapter, projectId]
  );
};
